import {
  type NotificationAudienceItem,
  type NotificationAudienceKind,
  type NotificationAudienceSpec,
  NOTIFICATION_AUDIENCE_KINDS,
} from '@utazzo/shared';
import {
  UzBadge,
  UzButton,
  type UzColumn,
  UzConfirmDialog,
  UzDrawer,
  UzInput,
  UzPagination,
  UzSelect,
  UzSwitch,
  UzTable,
  UzTextarea,
} from '@utazzo/ui';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import {
  useCreateNotificationAudience,
  useDeleteNotificationAudience,
  useNotificationAudiences,
  useUpdateNotificationAudience,
} from '../../use-notifications';

const KIND_LABEL: Record<NotificationAudienceKind, string> = {
  ROLES: 'Roles',
  USERS: 'Users',
  RESOLVER: 'Resolver',
  EVENT_PAYLOAD_FIELD: 'Event payload field',
  BRANCH: 'Branch',
};

const KIND_OPTIONS = NOTIFICATION_AUDIENCE_KINDS.map((k) => ({
  value: k,
  label: KIND_LABEL[k],
}));

export const NotificationAudiencesTab = (): React.JSX.Element => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [kindFilter, setKindFilter] = useState<string>('');
  const [editing, setEditing] = useState<NotificationAudienceItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<NotificationAudienceItem | null>(null);

  const query = {
    page,
    pageSize,
    sortDir: 'asc' as const,
    sortBy: 'code',
    ...(kindFilter ? { kind: kindFilter as NotificationAudienceKind } : {}),
  };
  const audiencesQuery = useNotificationAudiences(query);
  const updateAudience = useUpdateNotificationAudience();
  const deleteAudience = useDeleteNotificationAudience();

  const data = audiencesQuery.data?.data ?? [];

  const columns: ReadonlyArray<UzColumn<NotificationAudienceItem>> = [
    {
      id: 'code',
      header: 'Code',
      cell: (row) => <span className="font-mono text-xs text-zinc-800">{row.code}</span>,
      width: '200px',
    },
    {
      id: 'name',
      header: 'Name',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-sm text-zinc-900">{row.name}</span>
          {row.description && <span className="text-xs text-zinc-500">{row.description}</span>}
        </div>
      ),
    },
    {
      id: 'kind',
      header: 'Kind',
      cell: (row) => (
        <UzBadge tone="secondary" pill>
          {KIND_LABEL[row.kind]}
        </UzBadge>
      ),
      width: '180px',
    },
    {
      id: 'spec',
      header: 'Spec summary',
      cell: (row) => (
        <span className="font-mono text-[11px] text-zinc-600">{summarizeSpec(row.spec)}</span>
      ),
    },
    {
      id: 'active',
      header: 'Active',
      cell: (row) => (
        <UzSwitch
          checked={row.isActive}
          disabled={updateAudience.isPending}
          onCheckedChange={(checked) =>
            updateAudience.mutate({ id: row.id, payload: { isActive: checked } })
          }
        />
      ),
      width: '80px',
    },
    {
      id: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex justify-end gap-1">
          <UzButton variant="ghost" size="sm" onClick={() => setEditing(row)} title="Edit">
            <Pencil className="h-4 w-4" />
          </UzButton>
          <UzButton variant="ghost" size="sm" onClick={() => setDeleting(row)} title="Delete">
            <Trash2 className="h-4 w-4" />
          </UzButton>
        </div>
      ),
      width: '100px',
      align: 'right',
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <UzSelect
          label="Kind"
          value={kindFilter}
          onValueChange={(v: string) => {
            setPage(1);
            setKindFilter(v);
          }}
          options={[{ value: '', label: 'All kinds' }, ...KIND_OPTIONS]}
          containerClassName="w-56"
        />
        <div className="ml-auto">
          <UzButton size="sm" onClick={() => setCreating(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            New audience
          </UzButton>
        </div>
      </div>
      <UzTable
        columns={columns}
        data={data}
        getRowId={(row) => row.id}
        loading={audiencesQuery.isLoading}
      />
      <UzPagination
        page={page}
        pageSize={pageSize}
        totalItems={audiencesQuery.data?.pagination.totalItems ?? 0}
        onPageChange={setPage}
        pageSizeOptions={[20, 50, 100]}
        onPageSizeChange={(s) => {
          setPage(1);
          setPageSize(s);
        }}
      />

      {creating && <AudienceFormDrawer mode="create" onClose={() => setCreating(false)} />}
      {editing && (
        <AudienceFormDrawer mode="edit" initial={editing} onClose={() => setEditing(null)} />
      )}

      <UzConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete audience?"
        description={`"${deleting?.name ?? ''}" will be removed. Any rule that referenced it will start failing to resolve.`}
        confirmLabel="Delete"
        tone="destructive"
        onConfirm={() => {
          if (deleting) deleteAudience.mutate(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
};

/**
 * Drawer for create + edit. The spec sub-form swaps based on the
 * selected `kind` so the operator only sees the fields the server
 * will accept for that discriminant.
 */
interface AudienceFormDrawerProps {
  mode: 'create' | 'edit';
  initial?: NotificationAudienceItem;
  onClose: () => void;
}

const AudienceFormDrawer = ({
  mode,
  initial,
  onClose,
}: AudienceFormDrawerProps): React.JSX.Element => {
  const create = useCreateNotificationAudience();
  const update = useUpdateNotificationAudience();
  const pending = create.isPending || update.isPending;

  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [kind, setKind] = useState<NotificationAudienceKind>(initial?.kind ?? 'ROLES');
  // Spec is held as separate scratchpad strings per kind; only the
  // ones matching `kind` flow into the submit payload.
  const [rolesText, setRolesText] = useState(
    initial?.spec.kind === 'ROLES' ? initial.spec.roles.join(', ') : '',
  );
  const [usersText, setUsersText] = useState(
    initial?.spec.kind === 'USERS' ? initial.spec.userIds.join('\n') : '',
  );
  const [resolverKey, setResolverKey] = useState(
    initial?.spec.kind === 'RESOLVER' ? initial.spec.resolverKey : '',
  );
  const [field, setField] = useState(
    initial?.spec.kind === 'EVENT_PAYLOAD_FIELD' ? initial.spec.field : '',
  );
  const [branchesText, setBranchesText] = useState(
    initial?.spec.kind === 'BRANCH' ? initial.spec.branchIds.join('\n') : '',
  );

  const buildSpec = (): NotificationAudienceSpec | null => {
    switch (kind) {
      case 'ROLES': {
        const roles = rolesText
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        if (roles.length === 0) return null;
        return { kind: 'ROLES', roles };
      }
      case 'USERS': {
        const userIds = usersText
          .split(/\s+/)
          .map((s) => s.trim())
          .filter(Boolean);
        if (userIds.length === 0) return null;
        return { kind: 'USERS', userIds };
      }
      case 'RESOLVER':
        if (!resolverKey.trim()) return null;
        return { kind: 'RESOLVER', resolverKey: resolverKey.trim() };
      case 'EVENT_PAYLOAD_FIELD':
        if (!field.trim()) return null;
        return { kind: 'EVENT_PAYLOAD_FIELD', field: field.trim() };
      case 'BRANCH': {
        const branchIds = branchesText
          .split(/\s+/)
          .map((s) => s.trim())
          .filter(Boolean);
        if (branchIds.length === 0) return null;
        return { kind: 'BRANCH', branchIds };
      }
    }
  };

  const submit = (): void => {
    const spec = buildSpec();
    if (!spec) return;
    if (mode === 'create') {
      create.mutate(
        {
          code: code.trim(),
          name: name.trim(),
          description: description.trim() || undefined,
          spec,
        },
        { onSuccess: onClose },
      );
    } else if (initial) {
      update.mutate(
        {
          id: initial.id,
          payload: {
            code: code.trim(),
            name: name.trim(),
            description: description.trim() || undefined,
            spec,
          },
        },
        { onSuccess: onClose },
      );
    }
  };

  return (
    <UzDrawer
      open
      onOpenChange={(o) => !o && onClose()}
      title={mode === 'create' ? 'New audience' : `Edit ${initial?.name ?? ''}`}
      description="Reusable target group for one or more rules."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={onClose} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={submit} disabled={pending || !code.trim() || !name.trim()}>
            {mode === 'create' ? 'Create' : 'Save'}
          </UzButton>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <UzInput
          label="Code"
          placeholder="lowercase_snake_case"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={pending || mode === 'edit'}
          required
        />
        <UzInput
          label="Display name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={pending}
          required
        />
        <UzTextarea
          label="Description"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={pending}
        />
        <UzSelect
          label="Kind"
          value={kind}
          onValueChange={(v: NotificationAudienceKind) => setKind(v)}
          options={KIND_OPTIONS}
          disabled={pending || mode === 'edit'}
        />
        {kind === 'ROLES' && (
          <UzInput
            label="Roles (comma-separated)"
            placeholder="ADMIN, OPS, SALES_LEAD"
            value={rolesText}
            onChange={(e) => setRolesText(e.target.value)}
            disabled={pending}
          />
        )}
        {kind === 'USERS' && (
          <UzTextarea
            label="User UUIDs (one per line)"
            rows={4}
            value={usersText}
            onChange={(e) => setUsersText(e.target.value)}
            disabled={pending}
          />
        )}
        {kind === 'RESOLVER' && (
          <UzInput
            label="Resolver key"
            placeholder="leadOwnerAndCreator"
            value={resolverKey}
            onChange={(e) => setResolverKey(e.target.value)}
            disabled={pending}
          />
        )}
        {kind === 'EVENT_PAYLOAD_FIELD' && (
          <UzInput
            label="Payload field path"
            placeholder="assignedAgentId"
            value={field}
            onChange={(e) => setField(e.target.value)}
            disabled={pending}
            hint="Dot-path resolving to a UUID or array of UUIDs in the event payload."
          />
        )}
        {kind === 'BRANCH' && (
          <UzTextarea
            label="Branch UUIDs (one per line)"
            rows={4}
            value={branchesText}
            onChange={(e) => setBranchesText(e.target.value)}
            disabled={pending}
          />
        )}
      </div>
    </UzDrawer>
  );
};

const summarizeSpec = (spec: NotificationAudienceSpec): string => {
  switch (spec.kind) {
    case 'ROLES':
      return spec.roles.join(', ');
    case 'USERS':
      return `${spec.userIds.length} user(s)`;
    case 'RESOLVER':
      return spec.resolverKey;
    case 'EVENT_PAYLOAD_FIELD':
      return `payload.${spec.field}`;
    case 'BRANCH':
      return `${spec.branchIds.length} branch(es)`;
  }
};
